import { useState, useEffect, useRef } from "react";
import axios from "axios";
import TableC from "../../features/table/Table";
import SideMenu from "../../features/sideMenu/SideMenu";
import Activo from "../../assets/img/svg/users/activo.svg";
import Inactivo from "../../assets/img/svg/users/inactivo.svg";
import Ajustes from "../../assets/img/svg/users/opciones.svg";
import Eliminar from "../../assets/img/svg/users/eliminar.svg";
import Restablecer from "../../assets/img/svg/users/restablecer.svg";
import Editar from "../../assets/img/svg/users/editar.svg";
import { fetchPerfiles } from "../functions/Functions";
import { useNavigate } from "react-router-dom";
import useConfirmDialog from "../../features/modal/ConfirmDialog";
import DesactivarIcon from "../../assets/img/svg/modal/inactivo.svg";
import ActivarIcon from "../../assets/img/svg/modal/activo.svg";
import Vector from "../../assets/img/svg/users/Vector.svg";
import "./Users.css";

const Users = ({ API_URL }) => {
  const [data, setData] = useState([]);
  const [dataOriginal, setDataOriginal] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState({ top: 0, left: 0 }); // Estado para el menú desplegable
  const [perfiles, setPerfiles] = useState([]); // Almacenar perfiles
  const [activeSelectIndex, setActiveSelectIndex] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.post(
        `${API_URL}/api/usuarios-por-criterios`,
        {
          Usuario: {
            id: null,
            nombre: null,
            perfil: null,
            userName: null,
            lenguaje: null,
            correo: null,
          },
        },
        { withCredentials: true }
      );

      const sortedData = response.data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre)
      );

      setData(sortedData);
      setDataOriginal(sortedData);
    };

    fetchPerfiles(API_URL, setPerfiles);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDialog = useConfirmDialog();

  // Función para deshabilitar un usuario
  const disableUser = (userId, name, index) => {
    confirmDialog({
      html: `<p class="text-dialog">${name} será inhabilitado. <br>Podrás cambiar su estatus en cualquier momento.</p>`,
      icon: "warning",
      iconHtml: `<img src="${DesactivarIcon}" width="50" height="50" />`,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      onConfirm: async () => {
        try {
          await axios
            .post(
              `${API_URL}/api/usuario/deshabilitar-usuario`,
              {},
              {
                params: { id: userId },
                withCredentials: true,
                headers: {
                  "Content-Type": "application/json",
                },
              }
            )
            .then((result) => {
              if (result.status === 200) {
                setData((prevData) => {
                  const updatedData = [...prevData];
                  updatedData[index].habilitado = false;
                  return updatedData;
                });
              }
            });
        } catch (error) {
          console.error("Error al deshabilitar usuario:", error);
        }
      },
    });
  };

  // Función para habilitar un usuario
  const enableUser = async (userId, name, index) => {
    confirmDialog({
      html: `<p class="text-dialog">${name} será activado. <br> Podrás cambiar su estatus en cualquier momento.</p>`,
      icon: "success",
      iconHtml: `<img src="${ActivarIcon}" width="50" height="50" />`,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      onConfirm: async () => {
        try {
          await axios
            .post(
              `${API_URL}/api/usuario/habilitar-usuario`,
              {},
              {
                params: { id: userId },
                withCredentials: true,
                headers: {
                  "Content-Type": "application/json",
                },
              }
            )
            .then((result) => {
              if (result.status === 200) {
                setData((prevData) => {
                  const updatedData = [...prevData];
                  updatedData[index].habilitado = true;
                  return updatedData;
                });
              }
            });
        } catch (error) {
          console.error("Error al habilitar usuario:", error);
        }
      },
    });
  };

  const restablecerUsuario = async (userId, name) => {
    confirmDialog({
      html: `<p class="text-dialog">Se restablecerá la cuenta de <strong>${name}</strong>. <br> ¿Deseas continuar?</p>`,
      icon: "warning",
      iconHtml: `<img src="${ActivarIcon}" width="50" height="50" />`,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${API_URL}/api/usuario-restablecer/${userId}`,
            {}, // Payload opcional
            {
              withCredentials: true,
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          if (response.status === 200) {
            console.log("Usuario restablecido correctamente.");
          }
        } catch (error) {
          console.error(
            "Error al restablecer usuario:",
            error.response?.data || error.message
          );
        }
      },
    });
  };

  const editarUsuario = async (id) => {
    navigate(`/usuarios/editar/${id}`);
  };

  const rowIconRef = useRef([]); // Ref único para cada fila
  const [isOn, setIsOn] = useState(false);
  const toggleMenuOptions = (index) => {
    setOpenMenuIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const toggleSelect = (index) => {
    setActiveSelectIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const updateEstatus = (index, newValue, userId, name) => {
    const updatedData = [...data];
    // updatedData[index].habilitado = newValue;
    setData(updatedData);
    if (newValue) {
      enableUser(userId, name, index);
    } else {
      disableUser(userId, name, index);
    }
    setActiveSelectIndex(null);
  };

  const columns = [
    {
      Header: "Nombre",
      accessor: "nombre",
      width: 150,
    },
    {
      Header: "Perfil",
      accessor: "perfil.descripcion",
      width: 130,
    },
    {
      Header: "Correo",
      accessor: "correo",
      width: 130,
    },
    {
      Header: "Estatus",
      accessor: "habilitado",
      width: 100,
      Cell: ({ value, row }) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {activeSelectIndex === row.index ? (
            <div
              style={{
                position: "absolute",
                padding: "7px",
                top: "100%",
                left: 0,
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                zIndex: 10,
              }}
            >
              <div
                style={{
                  padding: "5px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() =>
                  updateEstatus(
                    row.index,
                    true,
                    data[row.index].id,
                    data[row.index].nombre
                  )
                }
              >
                <img
                  src={Activo}
                  alt="Activo"
                  style={{ width: "20px", height: "20px", marginRight: "8px" }}
                />
                Activo
              </div>
              <div
                style={{
                  padding: "5px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() =>
                  updateEstatus(
                    row.index,
                    false,
                    data[row.index].id,
                    data[row.index].nombre
                  )
                }
              >
                <img
                  src={Inactivo}
                  alt="Inactivo"
                  style={{ width: "20px", height: "20px", marginRight: "8px" }}
                />
                Inhabilitado
              </div>
            </div>
          ) : (
            <div
              className="user-status"
              style={{
                width: "40px",
                height: "30px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => toggleSelect(row.index)}
            >
              <img
                src={value ? Activo : Inactivo}
                alt={value ? "Activo" : "Inactivo"}
                style={{ width: "20px", height: "20px" }}
              />
              <img src={Vector} alt=" " className="arrow-icon" />
            </div>
          )}
        </div>
      ),
    },
    {
      Header: " ",
      width: 50,
      Cell: ({ row }) => {
        return (
          <div style={{ position: "relative" }}>
            <button
              ref={(el) => (rowIconRef.current[row.index] = el)}
              onClick={() => toggleMenuOptions(row.index)}
              className="action-icon"
            >
              <img
                src={Ajustes}
                alt="Ajustes"
                style={{ width: "20px", height: "20px" }}
              />
            </button>
            {openMenuIndex === row.index && (
              <div
                className="dropdown-menu-user"
                style={{
                  top: `${openMenuIndex.top}px`,
                  left: `${openMenuIndex.left}px`,
                }}
              >
                <p
                  onClick={() =>
                    restablecerUsuario(
                      data[row.index].id,
                      data[row.index].nombre
                    )
                  }
                >
                  <img src={Restablecer} alt="" /> Restablecer
                </p>
                <p
                  onClick={() => {
                    updateEstatus(
                      row.index,
                      false,
                      data[row.index].id,
                      data[row.index].nombre
                    );
                    toggleMenuOptions(row.index);
                  }}
                >
                  <img src={Eliminar} alt="" /> Eliminar usuario
                </p>
                <p onClick={() => editarUsuario(data[row.index].id)}>
                  <img src={Editar} alt="" /> Editar usuario
                </p>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const fields = [
    { name: "nombre", label: "Nombre" },
    { name: "userName", label: "Usuario" },
    { name: "perfil", label: "Perfil" },
    { name: "buscarInhabilitados", label: "¿Buscar deshabilitados?" },
  ];

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <div style={{ width: "12rem" }}>
        <SideMenu />
      </div>
      <div style={{ flex: 1, padding: "10px", width: "80%" }}>
        <TableC
          data={data}
          dataOriginal={dataOriginal}
          setData={setData}
          columns={columns}
          rowsPerPage={6}
          url={"usuarios-por-criterios"}
          fields={fields}
          perfiles={perfiles}
          isOn={isOn}
          setIsOn={setIsOn}
          width={"252px"}
          height={"28px"}
          navigateBTNew={"/usuarios/nuevo"}
        />
      </div>
      ;
    </div>
  );
};

export default Users;
